import { isClerkAPIResponseError, useClerk } from "@clerk/clerk-expo";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import { useCallback, useContext } from "react";

import { LayoutContext } from "@/components/LayoutProvider";
import { useAnalytics } from "@/hooks/analytics";
import { clearPartnerIntent } from "@/hooks/usePartnerIntent";
import { captureMessage } from "@/sentry";
import { useConversationComposition } from "@/store/conversation";
import { resetUser as resetCustomerIO } from "@/utils/customerIO";

export default function useLogout() {
  const queryClient = useQueryClient();
  // Logout intentionally uses the router without propagated tracking params to lose them on logout
  const router = useRouter();
  const { signOut } = useClerk();
  const { reset: resetAnalytics } = useAnalytics();
  const { resetAll: resetConversationComposition } =
    useConversationComposition();
  const { setModalContent } = useContext(LayoutContext);

  const logout = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      const isAlreadySignedOut =
        isClerkAPIResponseError(error) &&
        error.errors.some((e) => e.code === "signed_out");
      if (!isAlreadySignedOut) {
        captureMessage(`Error signing out: ${error}`);
      }
    } finally {
      resetAnalytics();
      resetCustomerIO();
      resetConversationComposition();
      setModalContent(null);
      queryClient.clear();
      await clearPartnerIntent();
      router.replace("/");
    }
  }, [
    resetAnalytics,
    resetConversationComposition,
    router,
    signOut,
    queryClient,
    setModalContent,
  ]);

  return logout;
}
